import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { text } from '../../shared/text';
import { ArrowLeft } from './icons';

/**
 * The shell every client screen sits in, and the two answers that are not
 * screens: loading, and not found.
 *
 * ## What was taken from the hi-fi, and what was left on the canvas
 *
 * Taken: the warm page, the 24 px side gutter, the app bar with a back control
 * on the left and an action on the right, and the display face on the title.
 *
 * **Left behind, deliberately: the phone.** `app.css` draws a `.phone` bezel, a
 * `.dynamic-island` and a `.status-bar` with `9:41` and a battery in it. Those
 * are how a mock is presented on a design canvas. On a real phone the operating
 * system draws the real status bar, and a painted one sits doubled on top of it;
 * on a laptop it is a picture of a phone around a web page. The design's own
 * README marks all three as chrome that must not be ported, and they are not.
 *
 * ## No tab bar, and why that is a question rather than an omission
 *
 * The hi-fi's `TabBar` has three tabs — Plan, Videos, Profile. `mvp-discovery` →
 * `client-app` describes five — Today · Week · Notes · Messages · You. That is an
 * information-architecture difference and the owner's call, not this change's:
 * `client-daily-loop`'s own spec says nothing about navigation at all.
 *
 * So this slice ships **no** tab bar. Four of the five destinations do not exist
 * yet — the week, the coach's notes, messages and the You screen are slices 2, 3
 * and 4 — and a bar of tabs that lead nowhere is worse than no bar: it promises
 * a product that is not there and it would have to be rebuilt the moment the
 * question is answered. Today is the home, and the session goes back to it.
 */
export function ClientPage({ children }: { children: ReactNode }) {
  return (
    <div className="bg-surface min-h-dvh">
      <main className="mx-auto w-full max-w-2xl px-6 pt-4 pb-20">{children}</main>
    </div>
  );
}

/**
 * Back, a title, and at most one action — the hi-fi's `AppBar`.
 *
 * The back control is a link rather than `history.back()`: a client who opened
 * a session from a notification, or reloaded it, still has somewhere to go.
 */
export function AppBar({
  backTo,
  backLabel,
  title,
  action,
}: {
  backTo?: string;
  /** What the back control says. It is a word, not only an arrow. */
  backLabel?: string;
  title?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-11 items-center gap-3 py-1">
      {backTo === undefined ? null : (
        <Link
          to={backTo}
          className="text-ink-soft -ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-[--radius-control] px-2 text-sm font-medium underline-offset-2 hover:underline"
        >
          <ArrowLeft size={20} />
          {backLabel}
        </Link>
      )}
      {title === undefined ? null : (
        <h1 className="text-ink font-display min-w-0 flex-1 truncate text-lg">{title}</h1>
      )}
      {action === undefined ? null : <div className="ml-auto shrink-0">{action}</div>}
    </div>
  );
}

/**
 * "Cargando…", in the product's language.
 *
 * `src/app/admin/AdminLayout.tsx` exports a `Loading` that says it in English,
 * and until now every client screen used it — so the first thing a Castilian
 * client saw on a slow connection was an English word. `src/shared/text/es.ts`
 * named that gap and said this change should close it. This is the client's
 * half; the admin keeps its own, because the coach's side is not translated and
 * putting it through the catalogue would add words no client ever reads.
 */
export function ClientLoading() {
  return (
    <ClientPage>
      <p className="text-ink-muted py-8 text-sm">{text('app.loading')}</p>
    </ClientPage>
  );
}

/**
 * The single answer to every attempt to reach something that is not yours.
 *
 * It says the same thing `src/ui/NotFound.tsx` says and it says it in Castilian.
 * It never names what was asked for: a session belonging to another client, a
 * session in a week that is still a draft, and a session id that never existed
 * must be indistinguishable (`access-and-consent`, "no hint that the content
 * exists").
 */
export function ClientNotFound() {
  return (
    <ClientPage>
      <h1 className="text-ink font-display mt-8 text-2xl">{text('client.notFound.title')}</h1>
      <p className="text-ink-soft mt-2 text-sm">{text('client.notFound.body')}</p>
    </ClientPage>
  );
}
